import * as rxjs from 'rxjs';
import * as Elevated from '@pitaman71/omniglot-introspect';
import { Dependencies, Mutations, Streams, Stores } from '.';

export class Client implements Stores.Persist {
    fetch: (url:string, config?:RequestInit) => Promise<Response>
    baseUrl: string;
    onPullRequest: rxjs.Subject<any>;

    constructor(
        fetch: (url:string, config?: RequestInit) => Promise<Response>, 
        baseUrl: string
    ) {
        this.fetch = fetch;
        this.baseUrl = baseUrl;
        this.onPullRequest = Dependencies.makeSubject();
    }

    _wait<T>(periodMs: undefined|number, cb: () => Promise<T>): Promise<T> {
        return new Promise((resolve, reject) => {
            setTimeout(() => cb().then(resolve).catch(reject), periodMs);
        })
    }

    _retry<T>(periodMs: undefined|number, maxTries: number, why: string, getNextPeriod: (prev: number|undefined) => number, cb: () => Promise<T>): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            cb().then(resolve).catch(error => {
                if(maxTries === 0) {
                    console.error(`Remote call failed, max retries exceeded: ${why}`);
                    reject(error);
                }
                console.error(`Remote call failed, will retry: ${why}`);
                const periodMs_ = getNextPeriod(periodMs);
                return this._wait(getNextPeriod(periodMs_), () => this._retry(periodMs_, maxTries - 1, why, getNextPeriod, cb));
            });
        })
    }
    clear() : Promise<void> {
        return this.fetch(this.baseUrl+'/clear').then(() => { return });
    }

    replay<MutationType extends Mutations.BaseMutation>(
        key: Stores.Key<any>,
        mutations: rxjs.Observer<MutationType>
    ): Promise<Elevated.Reference<MutationType>> {
        const body = JSON.stringify(Elevated.JSONMarshal.toJSON({ key }));
        return this.fetch(this.baseUrl+'/replay', {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                },
            body
        }).then((response) => { 
            return response.json()
        }).then(as_json => {
            const unpacked = Elevated.JSONMarshal.fromJSON(as_json);
            if(!unpacked.mutations) {
                return Promise.reject(`PULL (${body}) Response had no mutations ${JSON.stringify(as_json)}`);
            }
            let last: undefined|MutationType;
            for(let mutation of unpacked.mutations) {
                if(mutation instanceof Mutations.BaseMutation) {
                    mutations.next(mutation as any);
                    last = mutation as any;
                }                
            }
            return Elevated.Reference.from(last || null);
        });
    }

    pull<MutationType extends Mutations.BaseMutation> (
        key: Stores.Key<any>,
        since: Elevated.Reference<MutationType>,
        mutations: rxjs.Observer<MutationType>,
    ): Promise<Elevated.Reference<MutationType>> {
        const body = JSON.stringify(Elevated.JSONMarshal.toJSON({ key, since }));
        return this.fetch(this.baseUrl+'/pull', {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body
        }).then((response) => { 
            return response.json()
        }).then(as_json => {
            const unpacked = Elevated.JSONMarshal.fromJSON(as_json);
            if(!unpacked.mutations) {
                return Promise.reject(`PULL (${body}) Response had no mutations ${JSON.stringify(as_json)}`);
            }
            let last: undefined|MutationType;
            for(let mutation of unpacked.mutations) {
                if(mutation instanceof Mutations.BaseMutation) {
                    mutations.next(mutation as any);
                    last = mutation as any;
                }                
            }
            return Elevated.Reference.from(last || null);
        });
    }

    push<MutationType extends Mutations.BaseMutation> (
        key: Stores.Key<any>,
        ...suffix: MutationType[]
    ): Promise<MutationType> {
        const body = JSON.stringify(Elevated.JSONMarshal.toJSON({ key, suffix }));
        return this._retry(undefined, 10, body, (periodMs: number|undefined) => periodMs === undefined ? 100 : periodMs * 1.8, () => {
            return this.fetch(this.baseUrl+'/push', {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body
            }).then((response) => { 
                return response.json().catch(json_err => Promise.reject(`PUSH ${body} received non-JSON response ${response.body}`))
            }).then(as_json => {
                return suffix[suffix.length - 1];
            });
        });
    }

    normalize(key: Stores.Key<any>) : Promise<any> {
        return Promise.resolve();
    }
}

export class Server {
    persist: Stores.Persist;
    onPullRequest: rxjs.Subject<any>;

    constructor(persist: Stores.Persist) {
        this.persist = persist;
        this.onPullRequest = Dependencies.makeSubject();
    }

    clear() : Promise<void> {
        return this.persist.clear();
    }

    replay<MutationType extends Mutations.BaseMutation>(
        key: Stores.Key<any>
    ): Promise<MutationType[]> {
        const result: MutationType[] = [];
        return this.persist.replay(key, {
            next: (mutation: MutationType) => result.push(mutation),
            error: () => {},
            complete: () => {}
        }).then(() => result);
    }

    pull<MutationType extends Mutations.BaseMutation> (
        key: Stores.Key<any>,
        since: Elevated.Reference<MutationType>
    ): Promise<MutationType[]> {
        const result: MutationType[] = [];
        return this.persist.pull(key, since, {
            next: (mutation: MutationType) => result.push(mutation),
            error: () => {},
            complete: () => {}
        }).then(() => result);
    }

    push<MutationType extends Mutations.BaseMutation> (
        key: Stores.Key<any>,
        ...suffix: MutationType[]
    ): Promise<MutationType> {
        return this.persist.push(key, ...suffix);
    }

    normalize(key: Stores.Key<any>) : Promise<any> {
        return Promise.resolve();
    }
}
